
import { defineComponent, PropType } from 'vue'
import ContactAvatar from '@/components/prospects/ContactAvatar.vue'
import {
  channelDisplayLabel,
  channelMeta,
  formatRelativeTime,
  truncatePreview
} from '@/utils/inboxMessageFormat.js'

interface ProspectRow {
  id: string
  firstName?: string
  lastName?: string
  profilePhoto?: string | null
  humanHandoff?: boolean
  aiEnabled?: boolean
  lastMessage?: string | null
  lastMessageAt?: string | null
  messageChannel?: string | null
  socialPlatform?: string | null
  socialAccountId?: string | null
  socialAccountName?: string | null
  leadScore?: number
  leadGrade?: string
  unreadCount?: number
}

export default defineComponent({
  name: 'prospect-conversation-row',
  components: {
    ContactAvatar
  },
  props: {
    prospect: {
      type: Object as PropType<ProspectRow>,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    }
  },
  emits: ['select'],
  computed: {
    displayName(): string {
      const name = [this.prospect.firstName, this.prospect.lastName]
        .filter(Boolean)
        .join(' ')
        .trim()
      return name || 'Unknown'
    },
    preview(): string {
      return truncatePreview(this.prospect.lastMessage || '')
    },
    timeLabel(): string {
      return formatRelativeTime(this.prospect.lastMessageAt)
    },
    channel(): { icon: string; color: string; label: string } {
      return channelMeta(this.prospect)
    },
    channelLabel(): string {
      return channelDisplayLabel(this.prospect)
    },
    showChannelBadge(): boolean {
      return Boolean(
        this.prospect.messageChannel
          || this.prospect.socialPlatform
          || this.prospect.socialAccountName
      )
    },
    aiActive(): boolean {
      return !this.prospect.humanHandoff && this.prospect.aiEnabled !== false
    },
    gradeLabel(): string {
      const value = String(this.prospect.leadGrade || 'cold').toLowerCase()
      if (value === 'hot') return 'Hot'
      if (value === 'warm') return 'Warm'
      return 'Cold'
    },
    gradeBadgeClass(): string {
      const value = String(this.prospect.leadGrade || 'cold').toLowerCase()
      if (value === 'hot') return 'badge-light-danger'
      if (value === 'warm') return 'badge-light-warning'
      return 'badge-light-secondary'
    },
    unreadCount(): number {
      return Math.max(0, Number(this.prospect.unreadCount) || 0)
    },
    hasUnread(): boolean {
      return this.unreadCount > 0
    },
    unreadBadgeLabel(): string {
      return this.unreadCount > 99 ? '99+' : String(this.unreadCount)
    }
  }
})
